import React from 'react';
import {
  X,
  Save,
  UserCog,
  Mail,
  Plus,
  Building,
  Trash2,
  User,
  Shield,
  Power
} from 'lucide-react';
import { formatPersonName } from '../utils/formatName';

interface User {
  id: string;
  email: string;
  full_name: string | null;
  role: 'admin' | 'normal' | 'reviewer';
  is_active: boolean;
  is_internal: boolean;
  is_moral: boolean;
  last_login: string | null;
  created_at: string;
  updated_at: string;
  user_organization?: Array<{
    organization: {
      id: string;
      name: string;
      hexcode: string | null;
    };
    is_active: boolean;
    is_main: boolean;
  }>;
  user_extra_mails?: Array<{
    id: string;
    email: string | null;
    created_at: string;
  }>;
}

interface Organization {
  id: string;
  name: string;
}

interface EditUserModalProps {
  showModal: boolean;
  editingUser: User | null;
  editOption: 'email' | 'add_email' | 'organizations' | 'toggle_active' | null;
  editFormData: {
    newEmail: string;
    additionalEmail: string;
    selectedOrganizations: string[];
    primaryOrganizations: Record<string, boolean>;
    stagedEmails: string[];
    is_moral: boolean;
    is_internal: boolean;
  };
  organizations: Organization[];
  onClose: () => void;
  onEditOptionChange: (option: 'email' | 'add_email' | 'organizations' | 'toggle_active' | null) => void;
  onFormDataChange: (data: {
    newEmail: string;
    additionalEmail: string;
    selectedOrganizations: string[];
    primaryOrganizations: Record<string, boolean>;
    stagedEmails: string[];
    is_moral: boolean;
    is_internal: boolean;
  }) => void;
  onSave: () => void;
  onDeleteExtraEmail?: (emailId: string) => void;
  onAddEmailToStaging?: () => void;
  onRemoveStagedEmail?: (email: string) => void;
}

export function EditUserModal({
  showModal,
  editingUser,
  editOption,
  editFormData,
  organizations,
  onClose,
  onEditOptionChange,
  onFormDataChange,
  onSave,
  onDeleteExtraEmail,
  onAddEmailToStaging,
  onRemoveStagedEmail
}: EditUserModalProps) {
  if (!showModal || !editingUser) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={(e) => {
        // Only close if clicking the backdrop (not the modal content)
        if (e.target === e.currentTarget && !editOption) {
          onClose();
        }
      }}
    >
      <div className="bg-white p-6 rounded-lg shadow-xl max-w-2xl w-full mx-4">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <UserCog className="w-6 h-6" />
            Editar usuario: {formatPersonName(editingUser.full_name) || editingUser.email}
          </h2>
          <button
            onClick={() => {
              if (editOption) {
                // Reset form data to original values
                const activeOrgs = editingUser.user_organization?.filter(uo => uo.is_active) || [];
                const primaryOrgs: Record<string, boolean> = {};
                activeOrgs.forEach(uo => {
                  if (uo.is_main) {
                    primaryOrgs[uo.organization.id] = true;
                  }
                });
                onFormDataChange({
                  newEmail: '',
                  additionalEmail: '',
                  selectedOrganizations: activeOrgs.map(uo => uo.organization.id),
                  primaryOrganizations: primaryOrgs,
                  stagedEmails: [],
                  is_moral: editingUser.is_moral || false,
                  is_internal: editingUser.is_internal || false
                });
                onEditOptionChange(null);
              } else {
                onClose();
              }
            }}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {!editOption ? (
          // Option selection
          <div className="space-y-4">
            <p className="text-gray-600 mb-4">Seleccione qué desea editar:</p>
            
            <button
              onClick={() => onEditOptionChange('email')}
              className="w-full p-4 border border-gray-300 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-blue-600" />
                <div>
                  <h3 className="font-semibold">Modificar datos del usuario</h3>
                  <p className="text-sm text-gray-600">Cambiar el correo electrónico principal del usuario</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => onEditOptionChange('add_email')}
              className="w-full p-4 border border-gray-300 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-blue-600" />
                <div>
                  <h3 className="font-semibold">Agregar correo adicional</h3>
                  <p className="text-sm text-gray-600">Añadir un correo secundario para enviar notificaciones a múltiples direcciones</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => onEditOptionChange('organizations')}
              className="w-full p-4 border border-gray-300 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                <Building className="w-5 h-5 text-blue-600" />
                <div>
                  <h3 className="font-semibold">Gestionar organizaciones</h3>
                  <p className="text-sm text-gray-600">Agregar o quitar organizaciones asignadas al usuario</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => onEditOptionChange('toggle_active')}
              className="w-full p-4 border border-gray-300 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                <Power className="w-5 h-5 text-blue-600" />
                <div>
                  <h3 className="font-semibold">Activar/Desactivar usuario</h3>
                  <p className="text-sm text-gray-600">Cambiar el estado de activación del usuario sin eliminarlo</p>
                </div>
              </div>
            </button>
          </div>
        ) : (
          // Edit forms based on selected option
          <div>
            {editOption === 'email' && (
              <div className="space-y-4">
                <h3 className="font-semibold mb-2">Modificar datos del usuario</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Correo actual
                  </label>
                  <input
                    type="email"
                    value={editingUser.email}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nuevo correo electrónico <span className="text-gray-500 font-normal">(opcional)</span>
                  </label>
                  <input
                    type="email"
                    value={editFormData.newEmail}
                    onChange={(e) => onFormDataChange({ ...editFormData, newEmail: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="nuevo@ejemplo.com"
                  />
                </div>
                <div className="border-t pt-4">
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-gray-700">Tipo de persona ante el SAT</label>
                    <div className="flex items-center gap-4">
                      <button
                        type="button"
                        onClick={() => onFormDataChange({ ...editFormData, is_moral: false })}
                        className={`flex-1 py-2 px-4 rounded-lg border-2 transition-all ${
                          !editFormData.is_moral 
                            ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium' 
                            : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        Persona Física
                      </button>
                      <button
                        type="button"
                        onClick={() => onFormDataChange({ ...editFormData, is_moral: true })}
                        className={`flex-1 py-2 px-4 rounded-lg border-2 transition-all ${
                          editFormData.is_moral 
                            ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium' 
                            : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        Persona Moral
                      </button>
                    </div>
                  </div>

                  {/* Internal User Toggle */}
                  <div className="pt-4 border-t border-gray-200">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      <Shield className="w-4 h-4 inline mr-2" />
                      Estado del Usuario
                    </label>
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-800 mb-3">
                      <p>Los usuarios internos se ocultan de los reportes y métricas del dashboard.</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => onFormDataChange({ ...editFormData, is_internal: false })}
                        className={`flex-1 py-2 px-4 rounded-lg border-2 transition-all ${
                          !editFormData.is_internal 
                            ? 'border-green-500 bg-green-50 text-green-700 font-medium' 
                            : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        Usuario Normal
                      </button>
                      <button
                        type="button"
                        onClick={() => onFormDataChange({ ...editFormData, is_internal: true })}
                        className={`flex-1 py-2 px-4 rounded-lg border-2 transition-all ${
                          editFormData.is_internal 
                            ? 'border-orange-500 bg-orange-50 text-orange-700 font-medium' 
                            : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        Usuario Interno
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {editOption === 'add_email' && (
              <div className="space-y-4">
                <h3 className="font-semibold mb-2">Gestionar correos adicionales</h3>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                  <p>Agregue múltiples correos. Los nuevos correos se guardarán al hacer clic en "Guardar cambios".</p>
                </div>
                
                {/* Add email input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Agregar correo adicional
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={editFormData.additionalEmail}
                      onChange={(e) => onFormDataChange({ ...editFormData, additionalEmail: e.target.value })}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && onAddEmailToStaging) {
                          e.preventDefault();
                          onAddEmailToStaging();
                        }
                      }}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="adicional@ejemplo.com"
                    />
                    {onAddEmailToStaging && (
                      <button
                        type="button"
                        onClick={onAddEmailToStaging}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-1"
                      >
                        <Plus className="w-4 h-4" />
                        OK
                      </button>
                    )}
                  </div>
                </div>

                {/* All emails list */}
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Lista de correos:</p>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {/* Primary email */}
                    <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <span className="text-sm">{editingUser.email}</span>
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">Principal</span>
                    </div>
                    
                    {/* Existing extra emails */}
                    {editingUser.user_extra_mails && editingUser.user_extra_mails.map((extraMail) => (
                      <div key={extraMail.id} className="flex items-center justify-between p-2 bg-gray-50 rounded group">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-gray-400" />
                          <span className="text-sm">{extraMail.email}</span>
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">Adicional</span>
                        </div>
                        {onDeleteExtraEmail && (
                          <button
                            onClick={() => onDeleteExtraEmail(extraMail.id)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-red-600 hover:bg-red-50 rounded"
                            title="Eliminar correo adicional"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                    
                    {/* Staged emails */}
                    {editFormData.stagedEmails.map((email) => (
                      <div key={email} className="flex items-center justify-between p-2 bg-green-50 rounded">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-green-600" />
                          <span className="text-sm">{email}</span>
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">Por agregar</span>
                        </div>
                        {onRemoveStagedEmail && (
                          <button
                            onClick={() => onRemoveStagedEmail(email)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                            title="Quitar de la lista"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {/* Summary */}
                  <div className="mt-3 text-sm text-gray-600">
                    Total: {1 + (editingUser.user_extra_mails?.length || 0) + editFormData.stagedEmails.length} correo(s)
                    {editFormData.stagedEmails.length > 0 && (
                      <span className="text-green-600 ml-2">
                        ({editFormData.stagedEmails.length} nuevo{editFormData.stagedEmails.length > 1 ? 's' : ''})
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {editOption === 'organizations' && (
              <div className="space-y-4">
                <h3 className="font-semibold mb-2">Gestionar organizaciones</h3>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Organizaciones asignadas
                  </label>
                  {organizations.map(org => (
                    <div key={org.id} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={editFormData.selectedOrganizations.includes(org.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              onFormDataChange({
                                ...editFormData,
                                selectedOrganizations: [...editFormData.selectedOrganizations, org.id]
                              });
                            } else {
                              const newPrimaryOrgs = { ...editFormData.primaryOrganizations };
                              delete newPrimaryOrgs[org.id];
                              onFormDataChange({
                                ...editFormData,
                                selectedOrganizations: editFormData.selectedOrganizations.filter(id => id !== org.id),
                                primaryOrganizations: newPrimaryOrgs
                              });
                            }
                          }}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium">{org.name}</span>
                      </label>
                      {editFormData.selectedOrganizations.includes(org.id) && (
                        <div className="ml-6 mt-2">
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={editFormData.primaryOrganizations[org.id] || false}
                              onChange={(e) => {
                                onFormDataChange({
                                  ...editFormData,
                                  primaryOrganizations: {
                                    ...editFormData.primaryOrganizations,
                                    [org.id]: e.target.checked
                                  }
                                });
                              }}
                              className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                            />
                            <span className="text-sm text-gray-600">Organización principal</span>
                          </label>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {editFormData.selectedOrganizations.length === 0 && (
                  <p className="text-sm text-red-600">Debe seleccionar al menos una organización</p>
                )}
                {editFormData.selectedOrganizations.length > 0 && !Object.values(editFormData.primaryOrganizations).some(v => v) && (
                  <p className="text-sm text-amber-600">Recomendación: Seleccione al menos una organización principal</p>
                )}
              </div>
            )}

            {editOption === 'toggle_active' && (
              <div className="space-y-4">
                <h3 className="font-semibold mb-2">Activar/Desactivar usuario</h3>

                {/* Current status */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Estado actual:</p>
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${editingUser.is_active ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    <span className={`font-semibold ${editingUser.is_active ? 'text-green-700' : 'text-red-700'}`}>
                      {editingUser.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>
                </div>

                {/* Warning/Info message */}
                {editingUser.is_active ? (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm">
                    <p className="text-yellow-800 font-medium mb-2">⚠️ Al desactivar este usuario:</p>
                    <ul className="list-disc list-inside text-yellow-700 space-y-1">
                      <li>El usuario seguirá visible en el sistema</li>
                      <li>Se mantendrán todos sus documentos y datos</li>
                      <li>Podrá reactivarse en cualquier momento</li>
                      <li>No recibirá notificaciones ni se le asignarán nuevos requirements</li>
                    </ul>
                  </div>
                ) : (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
                    <p className="text-blue-800 font-medium mb-2">ℹ️ Al activar este usuario:</p>
                    <ul className="list-disc list-inside text-blue-700 space-y-1">
                      <li>El usuario volverá a estar activo en el sistema</li>
                      <li>Recibirá notificaciones según configuración</li>
                      <li>Se le podrán asignar nuevos requirements</li>
                    </ul>
                  </div>
                )}

                {/* Action info */}
                <div className="border-t pt-4">
                  <p className="text-sm text-gray-600 mb-3">
                    {editingUser.is_active
                      ? '¿Desea desactivar este usuario?'
                      : '¿Desea activar este usuario?'}
                  </p>
                  <p className="text-xs text-gray-500 italic">
                    Nota: Esto es diferente a eliminar el usuario. Los usuarios inactivos permanecen en el sistema.
                  </p>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  // Reset form data to original values
                  const activeOrgs = editingUser.user_organization?.filter(uo => uo.is_active) || [];
                  const primaryOrgs: Record<string, boolean> = {};
                  activeOrgs.forEach(uo => {
                    if (uo.is_main) {
                      primaryOrgs[uo.organization.id] = true;
                    }
                  });
                  onFormDataChange({
                    newEmail: '',
                    additionalEmail: '',
                    selectedOrganizations: activeOrgs.map(uo => uo.organization.id),
                    primaryOrganizations: primaryOrgs,
                    stagedEmails: [],
                    is_moral: editingUser.is_moral || false,
                    is_internal: editingUser.is_internal || false
                  });
                  onEditOptionChange(null);
                }}
                className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
              >
                Cancelar
              </button>
              <button
                onClick={onSave}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Save className="w-4 h-4" />
                Guardar cambios
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}